/**
 * The Home tab's Ctr Stats panel: the stats text and the filter scoping it (which
 * officers, which hours, "from now"), its placement and font size, the hourly and
 * per-zone figures the counter graph shows, and Copy Stats.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { HistorySnapshot, RosterState } from "@/types/api";
import { apiClient } from "@/api/client";
import { getStartHour } from "@/config/modeConfig";
import { withSessionHeader } from "@/utils/statsHeader";
import { lastAddFooterLines, withFooter, type FooterLines } from "@/utils/statsFooter";
import { clampStatsFont, loadStatsFont, saveStatsFont } from "@/utils/statsFont";
import { currentStatsMarker, nowStartTime } from "@/utils/liveStats";
import { EMPTY_STATS_FILTER, isStatsFilterActive, type StatsFilterValue } from "@/utils/statsFilter";
import { hourlyStats, statsMarkers, statsTimeMarkers, zoneStatEntries } from "@/components/tabs/home/statsText";
import { loadPref, savePref } from '@/utils/prefStorage';

interface StatsPanelOptions {
  state: RosterState | null;
  /** The version being viewed (the current one, or one picked from history). */
  displayedSnapshot: HistorySnapshot | null | undefined;
  accessMode: 'id' | 'browse';
  sessionId: string;
}

export function useStatsPanel({ state, displayedSnapshot, accessMode, sessionId }: StatsPanelOptions) {
  const [statsCopied, setStatsCopied] = useState(false);
  // The raw stats text panel is collapsed by default — it used to always sit
  // below the counter grid, permanently taking space the grid could otherwise
  // use to show more rows without scrolling. The Stats/Filter/Copy toolbar
  // (a single pinned footer, always visible — see the fixed bar below) stays
  // reachable either way; this only gates the text block itself.
  const [statsExpanded, setStatsExpanded] = useState(false);
  // Where the expanded stats panel opens on a desktop-width screen: up from the bottom
  // bar (it needs vertical room, so it eats rows) or as a tab down the right side.
  const [statsPlacement, setStatsPlacementState] = useState<'bottom' | 'side'>(() => {
    return loadPref('statsPlacement') === 'side' ? 'side' : 'bottom';
  });
  const setStatsPlacement = (v: 'bottom' | 'side') => {
    setStatsPlacementState(v);
    savePref('statsPlacement', v);
  };
  // Ctr Stats text size, adjusted on its own with A−/A+ so it stays readable when the page
  // is zoomed out to fit more counter rows.
  const [statsFontPx, setStatsFontPxState] = useState<number>(loadStatsFont);
  const setStatsFontPx = (px: number) => {
    const v = clampStatsFont(px);
    setStatsFontPxState(v);
    saveStatsFont(v);
  };
  // Single source of truth for "which officers / what time period the stats count" —
  // both the raw stats text and the hourly header numbers derive from the SAME
  // filtered text below, so turning this on updates both displays together instead
  // of leaving one stale and the other filtered.
  const [statsFilterRaw, setStatsFilterRaw] = useState<StatsFilterValue>(EMPTY_STATS_FILTER);
  // The "count from" time follows the clock (now, rounded down to the half hour) until
  // the user picks a time themselves or chooses the full shift — then their choice
  // sticks. Not applied when browsing a past session, where "now" means nothing.
  const [startAuto, setStartAuto] = useState(true);
  const [nowTick, setNowTick] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNowTick(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  const nowStart = useMemo(
    () => (accessMode === 'browse' ? null : nowStartTime(nowTick, getStartHour(state?.shift_type ?? 'morning'))),
    [accessMode, nowTick, state?.shift_type],
  );
  const statsFilter = useMemo<StatsFilterValue>(
    () => (startAuto && nowStart ? { ...statsFilterRaw, start: nowStart } : statsFilterRaw),
    [statsFilterRaw, startAuto, nowStart],
  );
  const setStatsFilter = useCallback((next: StatsFilterValue) => {
    setStatsFilterRaw(next);
    // Any hand-made change to the start time (including Reset / Full shift) ends the auto-follow.
    if (next.start !== statsFilter.start) setStartAuto(false);
  }, [statsFilter.start]);
  const useNowAsStart = useCallback(() => {
    setStartAuto(true);
    setStatsFilterRaw(prev => ({ ...prev, start: null }));
  }, []);
  const [filteredStatsText, setFilteredStatsText] = useState<string | null>(null);
  // Which filter `filteredStatsText` was computed for, so Copy can tell a current result
  // from a stale one that is still showing while a newer request is in flight.
  const [filteredStatsKey, setFilteredStatsKey] = useState<string | null>(null);

  // Parse HHMM time markers from stats text (e.g. "1000:", "1130:") — the set of
  // times offered in the filter's start/end pickers, always from the UNFILTERED
  // text so narrowing the window doesn't also shrink what you can pick next.
  const timeMarkers = useMemo(() => statsTimeMarkers(displayedSnapshot?.stats), [displayedSnapshot?.stats]);

  const totalStatsOfficers = useMemo(
    () => Object.keys(displayedSnapshot?.officer_schedules ?? {}).length,
    [displayedSnapshot?.officer_schedules],
  );
  const statsFilterActive = isStatsFilterActive(statsFilter, totalStatsOfficers);
  // Re-fetch the filtered stats text whenever the filter (or the underlying
  // roster) changes. Both displays below — the raw "Copy Stats" text and the
  // hourly header numbers atop the counter graph — read this SAME string, so
  // there is exactly one computation of "what do the numbers say" to keep in
  // sync, not two independently-filtered copies.
  const statsFilterRequest = useMemo(() => ({
    officer_keys: statsFilter.officerKeys ? Array.from(statsFilter.officerKeys) : undefined,
    start_time: statsFilter.start ?? undefined,
    end_time: statsFilter.end ?? undefined,
  }), [statsFilter]);
  const statsFilterKey = JSON.stringify(statsFilterRequest);
  useEffect(() => {
    if (!statsFilterActive) { setFilteredStatsText(null); setFilteredStatsKey(null); return; }
    let cancelled = false;
    apiClient.getFilteredStats(statsFilterRequest)
      .then(res => { if (!cancelled) { setFilteredStatsText(res.stats); setFilteredStatsKey(statsFilterKey); } })
      .catch(() => { if (!cancelled) { setFilteredStatsText(null); setFilteredStatsKey(null); } });
    return () => { cancelled = true; };
  }, [statsFilterActive, statsFilterRequest, statsFilterKey, displayedSnapshot?.officer_schedules, displayedSnapshot?.timestamp]);

  // The one source both displays read from: the filtered text while a filter is
  // active, else the plain unfiltered stats.
  const filteredStats = (statsFilterActive ? filteredStatsText : null) ?? displayedSnapshot?.stats ?? '';

  // The counter graph's numbers (hourly header, zone bands) always read the FULL,
  // unfiltered stats — the filter only scopes the Copy Stats text.
  const graphStats = displayedSnapshot?.stats ?? '';
  // Parse stats into hourly pivot columns (HH:00 only, drops :30 entries).
  const pivotedStats = useMemo(() => hourlyStats(graphStats), [graphStats]);

  // Every "HHMM: nn/nn" entry of the stats text with its zone line, :30 ones included —
  // exactly the entries the Manning Stats text shows (the backend already leaves out a
  // :30 whose zones match the hour before). Drives the per-zone bands under the grid.
  const zoneEntries = useMemo(() => zoneStatEntries(graphStats), [graphStats]);

  // Live "now" highlight: re-evaluated every 30s, so the emphasis moves on its own
  // as the clock passes each stats entry. Skipped for past (browse) sessions,
  // where "now" says nothing about the roster being viewed.
  const liveStatsMarker = useMemo(() => {
    if (accessMode === 'browse' || !filteredStats) return null;
    const markers = statsMarkers(filteredStats);
    return currentStatsMarker(markers, nowTick, getStartHour(state?.shift_type ?? 'morning'));
  }, [accessMode, filteredStats, nowTick, state?.shift_type]);

  // Shown and copied with a "<ACar|DCar> - <session id>" first line so pasted stats
  // say which roster they came from. Display/copy only: the pivot and live-marker
  // parsing above read the bare `filteredStats`.
  // The footnote closing the text (what the last add brought in) describes the roster as it is
  // now, so it is left off while a past version is being viewed.
  const footerLines = useMemo<FooterLines>(
    () => (displayedSnapshot && displayedSnapshot === state?.current_snapshot
      ? lastAddFooterLines(state?.last_added, state?.officer_metadata, Object.keys(displayedSnapshot.officer_schedules ?? {}))
      : { sos: '', ot: '' }),
    [displayedSnapshot, state?.current_snapshot, state?.last_added, state?.officer_metadata],
  );
  // Which footnote lines Copy Stats includes — each ticked on its own, remembered on this device.
  const [footerInclude, setFooterIncludeState] = useState<{ sos: boolean; ot: boolean }>(() => ({
    sos: loadPref('statsFooterSos') !== '0',
    ot: loadPref('statsFooterOt') !== '0',
  }));
  const setFooterInclude = (which: 'sos' | 'ot', on: boolean) => {
    setFooterIncludeState(prev => ({ ...prev, [which]: on }));
    savePref(which === 'sos' ? 'statsFooterSos' : 'statsFooterOt', on ? '1' : '0');
  };
  const copiedFooter = useMemo(
    () => [footerInclude.sos ? footerLines.sos : '', footerInclude.ot ? footerLines.ot : ''],
    [footerInclude, footerLines],
  );
  // The text shown in the panel: the footnote lines sit under it with their own checkboxes.
  const statsDisplayText = useMemo(() => {
    if (!filteredStats || !sessionId) return filteredStats;
    const label = state?.operation_mode === 'departure' ? 'DCar' : 'ACar';
    return withSessionHeader(filteredStats, label, sessionId);
  }, [filteredStats, sessionId, state?.operation_mode]);

  // Copy always matches the active filter. If the filtered text isn't ready for the
  // CURRENT filter yet (just changed, still loading, or the earlier request failed),
  // fetch it now rather than falling back to the unfiltered full-shift text.
  const handleCopyStats = useCallback(() => {
    const flash = () => { setStatsCopied(true); setTimeout(() => setStatsCopied(false), 2000); };
    const withHeader = (t: string) => {
      if (!t) return t;
      if (!sessionId) return withFooter(t, copiedFooter);
      return withFooter(withSessionHeader(t, state?.operation_mode === 'departure' ? 'DCar' : 'ACar', sessionId), copiedFooter);
    };
    if (statsFilterActive && filteredStatsKey !== statsFilterKey) {
      const p = apiClient.getFilteredStats(statsFilterRequest).then(r => withHeader(r.stats));
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        // A promise-backed item keeps the user-gesture alive across the request (iOS Safari).
        void navigator.clipboard.write([new ClipboardItem({ 'text/plain': p.then(t => new Blob([t], { type: 'text/plain' })) })]).then(flash);
      } else {
        void p.then(t => navigator.clipboard.writeText(t)).then(flash);
      }
      return;
    }
    if (!statsDisplayText) return;
    navigator.clipboard.writeText(withFooter(statsDisplayText, copiedFooter));
    flash();
  }, [statsDisplayText, statsFilterActive, filteredStatsKey, statsFilterKey, statsFilterRequest, sessionId, state?.operation_mode, copiedFooter]);

  return {
    statsCopied, statsExpanded, setStatsExpanded, statsPlacement, setStatsPlacement,
    statsFontPx, setStatsFontPx, startAuto, nowStart, statsFilter, setStatsFilter, useNowAsStart,
    statsTimeMarkers: timeMarkers, pivotedStats, zoneStatEntries: zoneEntries, liveStatsMarker,
    statsDisplayText, handleCopyStats, footerLines, footerInclude, setFooterInclude,
  };
}
